import {
  AppBase,
  baseFromDoc,
  FirestoreBaseDoc,
  makeDocConverter,
} from '../../base/firestore-model-base'
import type { DocumentReference, Timestamp } from 'firebase/firestore'

/**
 * PendingSchoolAccount（学校 仮登録申請）
 *
 * - `PendingSchoolAccountDoc`: Firestore に保存される形（snake_case / Timestamp）
 * - `PendingSchoolAccount`: アプリで扱う形（camelCase / Date）
 * - `id` / `path` は AppBase で定義（documentId / document は廃止）
 */

export type PendingSchoolAccountStatus = 'pending' | 'approved' | 'rejected'

export type SchoolType = 'public' | 'private' | 'national'

export type PendingSchoolAccountDoc = FirestoreBaseDoc & {
  // 学校情報
  school_type: SchoolType
  school_name: string
  postal_code: string
  prefecture: string
  city: string
  address: string
  phone: string
  principal: string
  description?: string | null

  // 学校担当者情報
  contact_name: string
  contact_email: string

  // 承認状態
  status: PendingSchoolAccountStatus
  approved_at?: Timestamp | null
  rejected_at?: Timestamp | null
  /** 承認した管理者への参照（admin_accounts/{id}） */
  approved_by?: DocumentReference | null
  /** 却下した管理者への参照（admin_accounts/{id}） */
  rejected_by?: DocumentReference | null
  rejection_reason?: string | null
}

export type PendingSchoolAccount = AppBase & {
  // 学校情報
  schoolType: SchoolType
  schoolName: string
  postalCode: string
  prefecture: string
  city: string
  address: string
  phone: string
  principal: string
  description?: string

  // 学校担当者情報
  contactName: string
  contactEmail: string

  // 承認状態
  status: PendingSchoolAccountStatus
  approvedAt?: Date
  rejectedAt?: Date
  /** 承認した管理者のUID（admin_accounts のドキュメントID） */
  approvedBy?: string | null
  /** 却下した管理者のUID（admin_accounts のドキュメントID） */
  rejectedBy?: string | null
  rejectionReason?: string
}

export const pendingSchoolAccountDocConverter = makeDocConverter<PendingSchoolAccountDoc>()

export function toPendingSchoolAccount(
  id: string,
  doc: PendingSchoolAccountDoc,
  path: string,
): PendingSchoolAccount {
  const base = baseFromDoc(id, doc, path)
  return {
    ...base,
    schoolType: doc.school_type,
    schoolName: doc.school_name,
    postalCode: doc.postal_code,
    prefecture: doc.prefecture,
    city: doc.city,
    address: doc.address,
    phone: doc.phone,
    principal: doc.principal,
    description: doc.description ?? undefined,

    contactName: doc.contact_name,
    contactEmail: doc.contact_email,

    status: doc.status,
    approvedAt: doc.approved_at ? doc.approved_at.toDate() : undefined,
    rejectedAt: doc.rejected_at ? doc.rejected_at.toDate() : undefined,
    approvedBy: doc.approved_by?.id ?? undefined,
    rejectedBy: doc.rejected_by?.id ?? undefined,
    rejectionReason: doc.rejection_reason ?? undefined,
  }
}

